import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import ContentHeader from "../components/ContentHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORE = "msp_admin_key";
const SITE = "https://mortgagesurvivalplaybook.com";
const AdminArticles = () => {
  const navigate = useNavigate();
  const [adminKey, setAdminKey] = useState(sessionStorage.getItem(KEY_STORE) || "");
  const [authed, setAuthed] = useState(false);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [li, setLi] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});

  const headers = useCallback(() => ({ Authorization: `Bearer ${adminKey}` }), [adminKey]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/articles`, { headers: headers() });
      setArticles(data);
      setAuthed(true);
      sessionStorage.setItem(KEY_STORE, adminKey);
      try { const r = await axios.get(`${API}/linkedin/status`, { headers: headers() }); setLi(r.data); } catch { /* optional */ }
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Invalid admin key"); setAuthed(false); }
      else toast.error("Failed to load articles");
    } finally { setLoading(false); }
  }, [adminKey, headers]);

  const connectLinkedIn = () => {
    window.open(`${API}/linkedin/connect?token=${encodeURIComponent(adminKey)}`, "_blank", "noopener");
  };

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("linkedin");
    if (p === "connected") toast.success("LinkedIn connected — auto-posting is on");
    if (p === "error") toast.error("LinkedIn connection failed — check API approval");
  }, []);

  useEffect(() => { if (adminKey) load(); /* eslint-disable-next-line */ }, []);

  const generateNow = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/admin/articles/generate`, {}, { headers: headers() });
      toast.success(`Generated: ${data.article.title}`);
      await load();
    } catch { toast.error("Generation failed"); }
    finally { setGenerating(false); }
  };

  const toggleStatus = async (a) => {
    const next = a.status === "published" ? "draft" : "published";
    try {
      await axios.put(`${API}/admin/articles/${a.id}`, { status: next }, { headers: headers() });
      toast.success(next === "published" ? "Published" : "Unpublished");
      await load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/articles/${a.id}`, { headers: headers() });
      toast.success("Deleted");
      await load();
    } catch { toast.error("Delete failed"); }
  };

  const copySocial = (platform, text, slug) => {
    const full = `${text}\n\n${SITE}/insights/${slug}`;
    navigator.clipboard.writeText(full)
      .then(() => toast.success(`${platform} post copied — paste it on your page`))
      .catch(() => toast.error("Copy failed"));
  };

  const startEdit = (a) => {    setEditingId(a.id);
    setDraft({ title: a.title, meta_description: a.meta_description, tags: (a.tags || []).join(", "), body_html: a.body_html });
  };

  const saveEdit = async (id) => {
    try {
      await axios.put(`${API}/admin/articles/${id}`, {
        title: draft.title,
        meta_description: draft.meta_description,
        tags: draft.tags.split(",").map((t) => t.trim()).filter(Boolean),
        body_html: draft.body_html,
      }, { headers: headers() });
      toast.success("Saved");
      setEditingId(null);
      await load();
    } catch { toast.error("Save failed"); }
  };

  if (!authed) {
    return (
      <div style={{ minHeight: "100vh" }}>
        <ContentHeader />
        <div className="hero-bg" />
        <div className="admin-gate" data-testid="admin-gate">
          <h1>Content Admin</h1>
          <p style={{ color: "var(--g1)", marginBottom: "1.4rem", fontSize: "0.9rem" }}>
            Enter your admin key to manage AI-generated articles.
          </p>
          <input
            className="admin-input"
            type="password"
            placeholder="Admin key"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="admin-key-input"
          />
          <button className="admin-btn" onClick={load} disabled={loading || !adminKey} data-testid="admin-login-btn">
            {loading ? "Checking…" : "Access Dashboard"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh" }} data-testid="admin-dashboard">
      <ContentHeader />
      <div className="hero-bg" />
      <div className="admin-wrap">
        <div className="admin-header">
          <h1>Content Agent — {articles.length} articles</h1>
          <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", flexWrap: "wrap" }}>
            {li?.configured && (li.connected
              ? <span className="admin-badge admin-badge-pub" data-testid="li-connected" style={{ fontSize: "0.72rem" }}>✓ LinkedIn connected</span>
              : <button className="admin-btn-ghost" onClick={connectLinkedIn} data-testid="li-connect">Connect LinkedIn</button>
            )}
            <button className="admin-btn-ghost" onClick={() => navigate("/insights")} data-testid="admin-view-blog">View Blog</button>
            <button className="admin-btn-ghost" onClick={() => navigate("/admin/social")} data-testid="admin-social-assistant">Reply Assistant</button>
            <button className="admin-btn-ghost" onClick={() => navigate("/admin/status")} data-testid="admin-status-link">Payments Status</button>
            <button className="admin-btn" onClick={generateNow} disabled={generating} data-testid="admin-generate-btn">
              {generating ? "Generating…" : "+ Generate Article Now"}
            </button>
          </div>
        </div>

        {loading && <p style={{ color: "var(--g1)" }}>Loading…</p>}

        {articles.map((a) => (
          <div className="admin-row" key={a.id} data-testid={`admin-row-${a.id}`}>
            <div className="admin-row-main">
              <div>
                <span className={`admin-badge ${a.status === "published" ? "admin-badge-pub" : "admin-badge-draft"}`}>
                  {a.status}
                </span>
              </div>
              <h3>{a.title}</h3>
              <div className="admin-row-meta">/{a.slug} · {(a.tags || []).join(", ")}</div>

              {a.social?.linkedin && editingId !== a.id && (
                <div className="admin-social" data-testid={`admin-social-${a.id}`}>
                  <span className="admin-social-label">Share:</span>
                  <button className="admin-btn-ghost" onClick={() => copySocial("LinkedIn", a.social.linkedin, a.slug)} data-testid={`copy-linkedin-${a.id}`}>Copy LinkedIn post</button>
                  {a.social.x && <button className="admin-btn-ghost" onClick={() => copySocial("X", a.social.x, a.slug)} data-testid={`copy-x-${a.id}`}>Copy X post</button>}
                  {a.social.facebook && <button className="admin-btn-ghost" onClick={() => copySocial("Facebook", a.social.facebook, a.slug)} data-testid={`copy-fb-${a.id}`}>Copy Facebook post</button>}
                </div>
              )}

              {editingId === a.id && (
                <div className="admin-edit" data-testid={`admin-edit-${a.id}`}>
                  <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Title" data-testid="edit-title" />
                  <input value={draft.meta_description} onChange={(e) => setDraft({ ...draft, meta_description: e.target.value })} placeholder="Meta description" data-testid="edit-meta" />
                  <input value={draft.tags} onChange={(e) => setDraft({ ...draft, tags: e.target.value })} placeholder="Tags (comma separated)" data-testid="edit-tags" />
                  <textarea rows={12} value={draft.body_html} onChange={(e) => setDraft({ ...draft, body_html: e.target.value })} placeholder="Body HTML" data-testid="edit-body" />
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className="admin-btn" onClick={() => saveEdit(a.id)} data-testid="edit-save">Save</button>
                    <button className="admin-btn-ghost" onClick={() => setEditingId(null)}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
            {editingId !== a.id && (
              <div className="admin-row-actions">
                <button className="admin-btn-ghost" onClick={() => navigate(`/insights/${a.slug}`)} data-testid={`admin-preview-${a.id}`}>Preview</button>
                <button className="admin-btn-ghost" onClick={() => startEdit(a)} data-testid={`admin-edit-btn-${a.id}`}>Edit</button>
                <button className="admin-btn-ghost" onClick={() => toggleStatus(a)} data-testid={`admin-toggle-${a.id}`}>
                  {a.status === "published" ? "Unpublish" : "Publish"}
                </button>
                <button className="admin-btn-ghost admin-btn-danger" onClick={() => remove(a)} data-testid={`admin-delete-${a.id}`}>Delete</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminArticles;
